// Path: ./src/app/pages/StoreUploadedImagesList/index.tsx
import { useEffect, useState } from "react";
import { adminApiService } from "../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";

import { CustomPagination } from "../../components/Pagination";
import { useLoading } from "../../../contextApi/LoadingContext";
import {
  Wrapper,
  FlyerItemImageWrapper,
  PaginationWrapper,
  Element,
} from "./styled";
import { GfFlyerItemImage, GfStore } from "@swagger/typescript-fetch-goflyer";
import { Box, Container, Stack, Typography } from "@mui/material";
import { makeStoreDisplayName } from "../../../utils/dtoUtil/makeStoreDisplayName";
import ImageWidthModal from "../../components/ImageWithModal";
import { routes } from "../../../utils/constant/navigationRoutes";

/**
 * React functional component representing the Store Uploaded Image List.
 * Displays a list of flyer item images for a specific store.
 * @returns {JSX.Element} The Store Uploaded Image List component.
 */
const StoreUploadedImageList = () => {
  const { showLoadingScreen } = useLoading();
  const params = useParams();
  const navigate = useNavigate();
  const [flyerItemImageData, setFlyerItemImageData] = useState<
    GfFlyerItemImage[] | []
  >([]);
  const [page, setPage] = useState(1);
  const [totalFlyerItemImageData, setTotalFlyerItemImageData] = useState(0);
  const [storeDetails, setStoreDetails] = useState<GfStore>();

  const [perPage, setPerPage] = useState(10);
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPage(value);
  };

  /**
   * Fetches the list of flyer item images for the specified store.
   * Updates the state with the fetched data.
   * @returns {void}
   */
  const fetchStoreUploadedImages = async () => {
    showLoadingScreen(true);
    const conditions: string[] = [];

    if (params.storeId && params.storeId.length) {
      conditions.push(`store.id||$eq||${params.storeId}`);
    } else {
      navigate(routes.storeUploadImage.base);
    }
    const adminDeals =
      await adminApiService.getManyBaseGfFlyerItemImageAdminControllerGfFlyerItemImage(
        undefined,
        undefined,
        conditions,
        undefined,
        [`updatedAt,DESC`],
        ["store"],
        perPage,
        0,
        page,
        undefined,
      );

    setTotalFlyerItemImageData(adminDeals?.total ? adminDeals?.total : 0);
    setFlyerItemImageData(adminDeals.data);
    showLoadingScreen(false);
  };

  useEffect(() => {
    fetchStoreUploadedImages();
  }, [page]);

  useEffect(() => {
    if (page === 1) {
      fetchStoreUploadedImages();
    } else {
      setPage(1);
    }
  }, [perPage]);

  const getStoreDetail = async (storeId: string) => {
    const storeResult =
      await adminApiService.getOneBaseGfStoreAdminControllerGfStore(storeId);

    if (!storeResult?.id) {
      navigate(routes.core.root);
    }
    setStoreDetails(storeResult);
  };
  useEffect(() => {
    params?.storeId && getStoreDetail(params?.storeId);
  }, [params]);

  return (
    <Wrapper data-testid="storeuploadedimagelist-test-id">
      {storeDetails?.id ? (
        <Typography variant="h4" component="h4" ml={4}>
          {makeStoreDisplayName(storeDetails)}
        </Typography>
      ) : (
        <></>
      )}
      {flyerItemImageData?.length ? (
        <Stack
          direction={"column"}
          justifyContent={"space-between"}
          spacing={20}
        >
          <FlyerItemImageWrapper>
            {flyerItemImageData.length > 0 &&
              flyerItemImageData?.map((value) => {
                return (
                  <Element
                    key={value.id}
                    data-testid="storeuploadedimagelist-test-id"
                  >
                    <ImageWidthModal
                      smallImageUrl={value.gfImage.src}
                      largeImageUrl={value.gfImage.src}
                      alt={value.createdAt.getDate().toString()}
                      data={value}
                    />
                  </Element>
                );
              })}
          </FlyerItemImageWrapper>
          <PaginationWrapper>
            <CustomPagination
              page={page}
              perPage={perPage}
              handlePageChange={handlePageChange}
              setPerPage={setPerPage}
              total={totalFlyerItemImageData}
            />
          </PaginationWrapper>
        </Stack>
      ) : (
        <Container sx={{ marginTop: 15 }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h4" component="h4">
              Empty
            </Typography>
          </Box>
        </Container>
      )}
    </Wrapper>
  );
};
export default StoreUploadedImageList;
